import type { QuestionDetail } from "@models/Dataset";
import type { DatasetMetric } from "@models/Metric";
import type { WidgetRollup } from "@models/Rollup";

/**
 * List-visibility filter, per the PRD: "List only metrics whose result type
 * is Percentage." Checked against the metric's own Metric population
 * (always present, even pre-save) rather than `source.resultType` (null
 * until a source is chosen) — an unresolved-but-intended-Percentage metric
 * must still appear in the list, just disabled (see isMetricEligibleForBarChart).
 */
export function metricHasPercentageResultType(metric: DatasetMetric): boolean {
  return metric.population.resultType === "percentage";
}

/**
 * Bar Chart selectability for a metric already filtered to Percentage
 * (see metricHasPercentageResultType): disabled only when its definition
 * isn't resolved yet. Unlike Scorecard's brand-mapping eligibility
 * (isMetricEligibleForBrandMapping in scorecard-config), a one-dimensional
 * Single rollup is NOT excluded here, since "a metric producing one result
 * creates a valid single-bar chart."
 */
export function isMetricEligibleForBarChart(
  metric: DatasetMetric,
): { eligible: boolean; reason?: string } {
  if (!metric.source) {
    return { eligible: false, reason: "This metric's definition isn't resolved yet." };
  }
  return { eligible: true };
}

/**
 * Whether resolving this metric into Bar Chart request params requires
 * fetching the underlying QuestionDetail. Stricter than the shared
 * metricNeedsQuestionDetailFetch (dataset-metric-representation-utils.ts):
 * Bar Chart's VALUE/VARIABLE focus needs the response's raw code (`.value`/
 * `.label`), not just a display label, so single_one_dimensional's "one" and
 * "rollup" submodes need the fetch too, unlike Scorecard (which extracts
 * cells by label from the full-question response and never sends a VALUE
 * focus request at all).
 */
export function barChartMetricNeedsQuestionDetailFetch(metric: DatasetMetric): boolean {
  const source = metric.source;
  if (!source) return false;
  switch (source.kind) {
    case "single_one_dimensional":
    case "single_grid_value_focus":
    case "single_grid_variable_focus":
      return true;
    case "multiple_one_dimensional":
    case "numeric":
      return false;
  }
}

export interface BarChartMetricFocusParams {
  focusMode?: string;
  focusEntity?: string;
  focusEntityDisplayLabel?: string;
  focusRollup?: WidgetRollup;
  focusRollupName?: string;
  /** single_one_dimensional's rollup mode only — see buildBarChartFocusParamsFromMetric. */
  categoryRollups?: WidgetRollup[];
}

function rollupSourceCategoryLabels(
  sourceIds: string[],
  questionDetail: QuestionDetail | null,
): string[] {
  return sourceIds
    .map((id) => questionDetail?.values.find((v) => v.id === id)?.value)
    .filter((code): code is number => code !== undefined)
    .map((code) => String(code));
}

export function buildBarChartFocusParamsFromMetric(
  metric: DatasetMetric,
  questionDetail: QuestionDetail | null,
): BarChartMetricFocusParams {
  const source = metric.source;
  if (!source) return {};

  switch (source.kind) {
    case "single_one_dimensional": {
      const focus = source.focus;
      if (focus.mode === "all") return {};
      if (focus.mode === "one") {
        const value = questionDetail?.values.find((v) => v.id === focus.valueId);
        return {
          focusMode: "VALUE",
          focusEntity: value ? String(value.value) : "",
          focusEntityDisplayLabel: value?.title || value?.label || focus.valueLabel,
        };
      }
      // No variable/brand axis to preserve here (unlike the grid case below),
      // so this is the same operation as the Question path's chart-level
      // Category Rollups — categoryRollups, not focusRollup.
      return {
        categoryRollups: [
          {
            id: focus.rollup.id,
            name: focus.rollup.name,
            calculationType: focus.rollup.calculationType,
            sourceCategoryLabels: rollupSourceCategoryLabels(focus.rollup.sourceIds, questionDetail),
          },
        ],
      };
    }

    case "multiple_one_dimensional":
    case "numeric":
      return {};

    case "single_grid_value_focus": {
      const focus = source.focus;
      if (focus.mode === "one") {
        const value = questionDetail?.values.find((v) => v.id === focus.valueId);
        return {
          focusMode: "VALUE",
          focusEntity: value ? String(value.value) : "",
          focusEntityDisplayLabel: value?.title || value?.label || focus.valueLabel,
        };
      }
      // mode === "rollup"
      return {
        focusMode: "VALUE",
        focusRollup: {
          id: focus.rollup.id,
          calculationType: focus.rollup.calculationType,
          sourceCategoryLabels: rollupSourceCategoryLabels(focus.rollup.sourceIds, questionDetail),
        },
        focusRollupName: focus.rollup.name,
      };
    }

    case "single_grid_variable_focus": {
      const focus = source.focus;
      const variable = questionDetail?.rowVariables.find((v) => v.id === focus.variableId);
      return {
        focusMode: "VARIABLE",
        focusEntity: variable?.label ?? "",
        focusEntityDisplayLabel: variable?.rowtitle || variable?.title || focus.variableLabel,
      };
    }
  }
}

// Chart-agnostic (Bar Chart + Line Chart both narrow their fetched response
// the same way) — lives in dataset-metric-representation-utils.ts, re-exported
// here for existing call sites.
export { filterFetchedDataToMetricResults } from "@utils/dataset-metric-representation-utils";
